import React, { useState } from 'react';
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';
import { DiscoveryResponse, ResponseStatus } from '../data';
interface DataTableProps {
  data: DiscoveryResponse[];
  sortConfig: {
    key: keyof DiscoveryResponse;
    direction: 'asc' | 'desc';
  } | null;
  onSort: (key: keyof DiscoveryResponse) => void;
  onRowClick?: (row: DiscoveryResponse) => void;
  selectedRowId?: string | null;
}
const StatusBadge = ({
  status
}: {
  status: ResponseStatus;
}) => {
  const styles: Record<ResponseStatus, string> = {
    Substantive: 'bg-emerald-900/30 text-emerald-400 border-emerald-800/50',
    'Objection Only': 'bg-rose-900/30 text-rose-400 border-rose-800/50',
    'References Documents': 'bg-blue-900/30 text-blue-400 border-blue-800/50',
    Amended: 'bg-amber-900/30 text-amber-400 border-amber-800/50',
    'Not Yet Received': 'bg-slate-800/50 text-slate-400 border-slate-700/50'
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${styles[status]} whitespace-nowrap`} data-id="element-54">
      {status}
    </span>;
};
export function DataTable({
  data,
  sortConfig,
  onSort,
  onRowClick,
  selectedRowId
}: DataTableProps) {
  const SortIcon = ({
    columnKey
  }: {
    columnKey: keyof DiscoveryResponse;
  }) => {
    if (sortConfig?.key !== columnKey) {
      return <ChevronsUpDown className="w-3.5 h-3.5 text-geniac-muted ml-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-55" />;
    }
    return sortConfig.direction === 'asc' ? <ChevronUp className="w-3.5 h-3.5 text-geniac-accent ml-1" data-id="element-56" /> : <ChevronDown className="w-3.5 h-3.5 text-geniac-accent ml-1" data-id="element-57" />;
  };
  const Th = ({
    label,
    columnKey,
    className = ''
  }: {
    label: string;
    columnKey: keyof DiscoveryResponse;
    className?: string;
  }) => <th scope="col" className={`px-4 py-3 text-left text-xs font-semibold text-geniac-muted uppercase tracking-wider cursor-pointer group hover:bg-geniac-hover select-none border-b border-geniac-border ${className}`} onClick={() => onSort(columnKey)} data-id="element-58">
      <div className="flex items-center" data-id="element-59">
        {label}
        <SortIcon columnKey={columnKey} data-id="element-60" />
      </div>
    </th>;
  return <div className="w-full h-full flex flex-col bg-geniac-bg" data-id="element-61">
      <div className="flex-1 overflow-auto" data-id="element-62">
        <table className="min-w-full table-fixed border-collapse" data-id="element-63">
          <thead className="bg-geniac-bg sticky top-0 z-10" data-id="element-64">
            <tr data-id="element-65">
              <Th label="Responding Party" columnKey="party" className="w-[18%]" data-id="element-66" />
              <Th label="Set" columnKey="set" className="w-[8%]" data-id="element-67" />
              <Th label="Number" columnKey="number" className="w-[12%]" data-id="element-68" />
              <Th label="Question" columnKey="question" className="w-[22%]" data-id="element-69" />
              <Th label="Response" columnKey="response" className="w-[25%]" data-id="element-70" />
              <Th label="Status" columnKey="statuses" className="w-[15%]" data-id="element-71" />
            </tr>
          </thead>
          <tbody className="bg-geniac-bg divide-y divide-geniac-border" data-id="element-72">
            {data.length === 0 ? <tr data-id="element-73">
                <td colSpan={6} className="px-6 py-12 text-center text-sm text-geniac-muted" data-id="element-74">
                  No responses match the current filters.
                </td>
              </tr> : data.map(row => <tr key={row.id} onClick={() => onRowClick && onRowClick(row)} className={`cursor-pointer transition-colors group ${selectedRowId === row.id ? 'bg-geniac-hover border-l-2 border-l-geniac-accent' : 'hover:bg-geniac-hover border-l-2 border-l-transparent'}`} data-id="element-75">
                  <td className="px-4 py-3 text-sm text-geniac-text truncate" data-id="element-76">
                    {row.party}
                  </td>
                  <td className="px-4 py-3 text-sm text-geniac-muted whitespace-nowrap" data-id="element-77">
                    {row.set}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-geniac-text whitespace-nowrap" data-id="element-78">
                    {row.number}
                  </td>
                  <td className="px-4 py-3 text-sm text-geniac-muted" data-id="element-79">
                    <div className="line-clamp-2 max-w-full" title={row.question} data-id="element-80">
                      {row.question}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-geniac-muted" data-id="element-81">
                    <div className="line-clamp-2 max-w-full" title={row.response} data-id="element-82">
                      {row.response || <span className="text-geniac-border italic" data-id="element-83">
                          No response text
                        </span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-geniac-muted" data-id="element-84">
                    <div className="flex flex-wrap gap-1.5" data-id="element-85">
                      {row.statuses.map(status => <StatusBadge key={status} status={status} data-id="element-86" />)}
                    </div>
                  </td>
                </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}